import { useCallback, useEffect, useMemo, useState } from "react";
import { createSim, deleteSim, listSims, updateSim } from "../../api/endpoints/sims.js";
import { listSimTypes } from "../../api/endpoints/simTypes.js";
import { buildSimListSubtitle, simSearchHaystack } from "../../pages/Sims/simUtils.js";

function useSimSelection(items, search, simTypeMap) {
  const [selectedId, setSelectedId] = useState(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((sim) => simSearchHaystack(sim).includes(q));
  }, [items, search]);

  useEffect(() => {
    if (visible.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!visible.some((sim) => sim.id === selectedId)) {
      setSelectedId(visible[0].id);
    }
  }, [visible, selectedId]);

  const selected = useMemo(
    () => items.find((sim) => sim.id === selectedId) ?? null,
    [items, selectedId],
  );

  const selectedSimType = useMemo(
    () => (selected?.simTypeId ? simTypeMap[selected.simTypeId] ?? null : null),
    [selected, simTypeMap],
  );

  return { visible, selected, selectedId, setSelectedId, selectedSimType };
}

export function useAllSims() {
  const [items, setItems] = useState([]);
  const [simTypes, setSimTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const simTypeMap = useMemo(
    () => Object.fromEntries(simTypes.map((type) => [type.id, type])),
    [simTypes],
  );

  const { visible, selected, selectedId, setSelectedId, selectedSimType } = useSimSelection(items, search, simTypeMap);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [data, types] = await Promise.all([listSims(), listSimTypes()]);
      setItems(data);
      setSimTypes(types);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const addSim = useCallback(async (payload) => {
    setSaving(true);
    try {
      const created = await createSim(payload);
      setItems((list) => [created, ...list]);
      setSelectedId(created.id);
      return created;
    } finally {
      setSaving(false);
    }
  }, []);

  const saveSim = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      const updated = await updateSim(id, payload);
      setItems((list) => list.map((sim) => (sim.id === id ? updated : sim)));
      return updated;
    } finally {
      setSaving(false);
    }
  }, []);

  const removeSim = useCallback(async (id) => {
    await deleteSim(id);
    setItems((list) => list.filter((sim) => sim.id !== id));
  }, []);

  const getSimTypeName = useCallback(
    (simTypeId) => simTypeMap[simTypeId]?.name ?? "—",
    [simTypeMap],
  );

  const getSimSubtitle = useCallback(
    (sim) => buildSimListSubtitle(sim, simTypeMap[sim?.simTypeId]),
    [simTypeMap],
  );

  return {
    items: visible,
    allItems: items,
    simTypes,
    selectedSimType,
    loading,
    search,
    setSearch,
    selected,
    selectedId,
    setSelectedId,
    addSim,
    saveSim,
    removeSim,
    saving,
    reload,
    getSimTypeName,
    getSimSubtitle,
  };
}

export function useSims() {
  return useAllSims();
}
